'use client';

import { __assign } from '../_virtual/_tslib.js';
import React__default, { useReducer, useState, useRef, useCallback, useEffect, Suspense } from 'react';
import { searchBarReducer, SearchBarContext } from './context/reducer.js';
import { initialSearchBar } from './context/index.js';
import { SearchBarContentWrapper, SearchBlockHolder, SearchBarWrapper, SearchBarContent, DropDownContentOverflow } from './SearchBar.css.js';
import translations from './translation.js';
import SearchBackground from './components/SearchBarBackground/index.js';
import SearchSection from './components/SearchSection/index.js';
import { DEFAULT_SEARCH_BAR_OPTIONS, VNW_SEARCH_JOB_CITY_COOKIE_NAME, RECENT_SEARCH } from './constants.js';
import useElementOnScreen from '../Utils/hooks/UseElementOnScreen/index.js';
import { ACTIONS } from './context/actions.js';
import { transformToLatin } from '../Utils/helpers.js';
import { getLocalStorage, buildSearchUrl, getDistrictIdFromLocations, changeLocation } from './searchbarHelpers.js';
import { EForceControDropdown } from './context/types.js';
import { setCookie } from '../Utils/cookie.js';
import useHog from '../PostHog/hooks/useHog/index.js';
import '../Utils/constants.js';
import '../UploadResumeModal/type.js';

var DropdownContent = React__default.lazy(function () { return import('./components/DropdownContent/index.js'); });
var SearchBar = function (props) {
    var isMobile = props.isMobile, searchBarOptions = props.searchBarOptions, pageName = props.pageName, langCode = props.langCode, optionList = props.optionList, _a = props.domain, domain = _a === void 0 ? '' : _a;
    var mergedSearchBarOptions = __assign(__assign({}, DEFAULT_SEARCH_BAR_OPTIONS), searchBarOptions);
    var _b = useReducer(searchBarReducer, initialSearchBar), state = _b[0], dispatch = _b[1];
    var isShow = state.isShow, keyword = state.keyword, locations = state.locations, forceControllDropdown = state.forceControllDropdown;
    var tracking = useHog().tracking;
    // State
    var _c = useState(false), isMountedDropdown = _c[0], setMountedDropdown = _c[1];
    var _d = useState(-1), initialWidth = _d[0], setInitialWidth = _d[1];
    var _e = useState(false), isMountedSearchBar = _e[0], setMountedSearchBar = _e[1];
    var searchBarContentRef = useRef(null);
    var searchBarWrapperRef = useRef(null);
    var dropdownContentOverflowRef = useRef(null);
    var isSearchOnScreen = useElementOnScreen({
        target: searchBarWrapperRef,
        options: {
            rootMargin: '0px 0px 0px 0px'
        }
    })[0];
    var t = useCallback(function (text) {
        var _a;
        if (props.langCode === 'en') {
            return text;
        }
        else {
            return (_a = translations[text]) !== null && _a !== void 0 ? _a : text;
        }
    }, [props.langCode]);
    var search = useCallback(function (params) {
        var _a;
        if (params === void 0) { params = {}; }
        var _b = params.withKeyword, withKeyword = _b === void 0 ? '' : _b, _c = params.withJobFunction, withJobFunction = _c === void 0 ? [] : _c, _d = params.onlyFillInput, onlyFillInput = _d === void 0 ? false : _d;
        var localStorageParams = getLocalStorage('params', '');
        // VNW-30947 remove special word
        var searchKeyword = (_a = keyword === null || keyword === void 0 ? void 0 : keyword.toLowerCase().replace(/[`~,/':;\\"\s]+/g, ' ')) !== null && _a !== void 0 ? _a : '';
        if (withKeyword !== '') {
            searchKeyword = withKeyword === null || withKeyword === void 0 ? void 0 : withKeyword.toLowerCase();
        }
        var searchUrl = t('/job-search/all-jobs');
        var latinKeyword = transformToLatin(searchKeyword).replace(/ +/g, ' ');
        if (searchKeyword !== latinKeyword && mergedSearchBarOptions.isNeedNavigate) {
            searchKeyword = latinKeyword;
        }
        var searchLocations = locations;
        searchUrl = "".concat(domain).concat(buildSearchUrl(searchKeyword, searchLocations, langCode));
        if (typeof window !== 'undefined' && searchKeyword === '') {
            if (localStorageParams !== undefined && localStorageParams !== null) {
                localStorageParams.query = '';
            }
            localStorage.setItem('params', JSON.stringify(localStorageParams));
        }
        // update cookies
        var locationsIds = searchLocations === null || searchLocations === void 0 ? void 0 : searchLocations.map(function (e) { return e.parentId; });
        if (locationsIds.length > 0) {
            setCookie(VNW_SEARCH_JOB_CITY_COOKIE_NAME, locationsIds === null || locationsIds === void 0 ? void 0 : locationsIds.join(','), 30);
        }
        else {
            setCookie(VNW_SEARCH_JOB_CITY_COOKIE_NAME, 'all', 30);
        }
        var districtId = getDistrictIdFromLocations(searchLocations);
        tracking({
            eventName: 'js_search_result_page:search_button_click',
            properties: {
                keyword: keyword,
                filterLocationCityId: locationsIds,
                filterLocationDistrictId: districtId
            }
        });
        if (mergedSearchBarOptions.isNeedNavigate) {
            // Delay 300ms for finished transition
            // (UX: make user feeling smooth when navigating to search page)
            setTimeout(function () {
                var _a;
                // update localStorage search params
                if (localStorageParams !== undefined && localStorageParams !== null) {
                    localStorageParams.query = searchKeyword;
                    localStorageParams.locationId = (_a = searchLocations.map(function (item) { return item.parentId; })) !== null && _a !== void 0 ? _a : [];
                    localStorageParams.locations = searchLocations;
                    localStorageParams.districtId = districtId;
                }
                localStorage.setItem('params', JSON.stringify(localStorageParams));
                changeLocation(searchUrl, false, onlyFillInput);
            }, 300);
        }
        else {
            // for page-container, the page should stay
            if (!onlyFillInput) {
                if (typeof window === 'undefined') {
                    return;
                }
                var params_1 = {
                    query: searchKeyword,
                    page: 0
                };
                params_1.locationId = searchLocations.map(function (item) { return item.parentId; }) || [];
                params_1.districtId = districtId || [];
                params_1.locations = searchLocations || [];
                if (withJobFunction && withJobFunction.length > 0) {
                    params_1.jobFunctionId = withJobFunction;
                }
                var searchEvent = new CustomEvent('SEARCH', {
                    detail: {
                        params: params_1,
                        from: 'AppShell > SearchBar > Search',
                        hideFilter: true,
                        clearCurrentParams: false
                    }
                });
                window.dispatchEvent(searchEvent);
                // await 500ms then get recent keyword
                setTimeout(function () {
                    var recentSearchList = getLocalStorage(RECENT_SEARCH, '');
                    dispatch({
                        type: ACTIONS.SET_RECENT_KEYWORDS,
                        payload: Array.isArray(recentSearchList) ? recentSearchList.reverse() : []
                    });
                }, 500);
            }
        }
        dispatch({
            type: ACTIONS.FORCE_CONTROL,
            payload: EForceControDropdown.CLOSE_ALL
        });
    }, [domain, keyword, langCode, locations, mergedSearchBarOptions.isNeedNavigate, optionList, t]);
    var handleAnimationEnd = function () {
        if (isShow) {
            return;
        }
        setMountedDropdown(false);
    };
    useEffect(function () {
        if (!isShow) {
            setMountedDropdown(false);
            return;
        }
        setMountedDropdown(forceControllDropdown === EForceControDropdown.JUST_SHOW_DROPDOWN_CONTENT || isShow);
    }, [forceControllDropdown, isShow]);
    useEffect(function () {
        if (initialWidth !== -1) {
            return;
        }
        var calculateInitialWidth = function () {
            var _a;
            if (!searchBarContentRef.current) {
                return;
            }
            var initWidth = (_a = searchBarContentRef === null || searchBarContentRef === void 0 ? void 0 : searchBarContentRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth;
            if (!mergedSearchBarOptions.isHomepage && !isMobile) {
                initWidth -= 2;
            }
            if (isMobile && pageName !== 'searchResult') {
                initWidth = 40;
            }
            if (isMobile && pageName === 'searchResult') {
                initWidth = -1;
            }
            setInitialWidth(initWidth);
        };
        setTimeout(function () {
            calculateInitialWidth();
        }, 300);
    }, [searchBarContentRef, initialWidth, mergedSearchBarOptions.isHomepage, isMobile, pageName]);
    useEffect(function () {
        if (!isShow) {
            return;
        }
        if (isSearchOnScreen) {
            return;
        }
        if (isMobile) {
            return;
        }
        dispatch({
            type: ACTIONS.CLOSE_DROP_DOWN
        });
    }, [isSearchOnScreen, isMobile, isShow]);
    var handleOnEventSearch = function (e) {
        var _a, _b, _c;
        var searchEvent = e;
        var query = (_c = (_b = (_a = searchEvent === null || searchEvent === void 0 ? void 0 : searchEvent.detail) === null || _a === void 0 ? void 0 : _a.params) === null || _b === void 0 ? void 0 : _b.query) !== null && _c !== void 0 ? _c : '';
        if (query === '' || isShow) {
            return;
        }
        dispatch({
            type: ACTIONS.SET_KEYWORD,
            payload: query
        });
    };
    useEffect(function () {
        var onWindowResize = function () {
            setInitialWidth(-1);
        };
        var setKeywordFromLocalStorage = function () {
            var queryKeyword = getLocalStorage('params', 'query');
            if (queryKeyword === '') {
                return;
            }
            dispatch({
                type: ACTIONS.SET_KEYWORD,
                payload: queryKeyword
            });
        };
        // This setTimeout is used for preventing layout shifting
        // when component first render
        setTimeout(function () {
            setMountedSearchBar(true);
            setKeywordFromLocalStorage();
        }, 50);
        // Listen on Resize to make initialWidth responsive
        window.addEventListener('resize', onWindowResize);
        window.addEventListener('SEARCH', handleOnEventSearch);
        return function () {
            window.removeEventListener('resize', onWindowResize);
            window.removeEventListener('SEARCH', handleOnEventSearch);
        };
    }, [searchBarOptions === null || searchBarOptions === void 0 ? void 0 : searchBarOptions.isNeedNavigate]);
    return (React__default.createElement(SearchBarContext.Provider, { value: {
            state: __assign({}, state),
            props: __assign(__assign({}, props), { searchBarOptions: mergedSearchBarOptions }),
            t: t,
            search: search,
            dispatch: dispatch
        } },
        React__default.createElement(SearchBarContentWrapper, { "data-cname": 'SearchBarContentWrapper', isHomepage: mergedSearchBarOptions.isHomepage, ref: searchBarContentRef },
            React__default.createElement(SearchBackground, null),
            React__default.createElement(SearchBlockHolder, { isMountedDropdown: isMountedDropdown, isShow: isShow, isHomepage: mergedSearchBarOptions.isHomepage, isMobile: isMobile }),
            React__default.createElement(SearchBarWrapper, { isShow: isShow, isMobile: isMobile, isHomepage: mergedSearchBarOptions.isHomepage, isMountedDropdown: isMountedDropdown, "data-id": 'search-bar', ref: searchBarWrapperRef },
                React__default.createElement(SearchBarContent, { "data-cname": 'SearchBarContent', isMounted: isMountedSearchBar, isMountedDropdown: isMountedDropdown, isShow: isShow, isMobile: isMobile, initialWidth: initialWidth, isHomepage: mergedSearchBarOptions.isHomepage },
                    React__default.createElement(SearchSection, { isMountedDropdown: isMountedDropdown })),
                isMountedDropdown ? (React__default.createElement(DropDownContentOverflow, { isHidden: forceControllDropdown !== EForceControDropdown.JUST_SHOW_DROPDOWN_CONTENT, isMobile: isMobile, isHomepage: mergedSearchBarOptions.isHomepage, ref: dropdownContentOverflowRef },
                    React__default.createElement(Suspense, { fallback: React__default.createElement(React__default.Fragment, null) },
                        React__default.createElement(DropdownContent, { dropdownContentOverflowRef: dropdownContentOverflowRef, onAnimationEnd: handleAnimationEnd })))) : null))));
};
var index = React__default.memo(SearchBar);

export { index as default };
//# sourceMappingURL=index.js.map
